// ProfilePicture.jsx
import React, { useRef, useState, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Loader2, Upload, User, AlertCircle, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function defaultImage() {
  return "https://cdn.builder.io/api/v1/image/assets/TEMP/09145b0c-9647-4bd8-b1a8-3bf232a30e4d?placeholderIfAbsent=true&apiKey=adec8590503446dea9751332b3a30a3d";
}

export default function ProfilePicture({ onUpdate, profilePhoto }) {
  const fileInputRef = useRef(null);
  const [currentPhoto, setCurrentPhoto] = useState(profilePhoto);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const previewUrlRef = useRef(null);

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Only update if profilePhoto is different and not a blob URL (preview)
    if (profilePhoto && !profilePhoto.startsWith('blob:')) {
      // Clean up old preview URL if it exists
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      // Only update if it's actually different
      if (profilePhoto !== currentPhoto) {
        setCurrentPhoto(profilePhoto);
      }
    } else if (!profilePhoto && currentPhoto && !currentPhoto.startsWith('blob:')) {
      // If profilePhoto is cleared and currentPhoto is not a preview, clear it
      setCurrentPhoto(null);
    }
  }, [profilePhoto]);

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Don't set touched here - only set it when user actually interacts with file picker
    if (fileInputRef.current && !isUploading) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    
    // Silently return if no file selected (user cancelled)
    // Only set touched if user actually opened picker but didn't select
    if (!file) {
      e.target.value = '';
      // Don't set touched on cancellation - user might just be browsing
      return;
    }
    
    // Validate file type - show error only for invalid selections
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid file type', {
        description: 'Please select an image file (JPEG, PNG, etc.)',
        icon: <AlertCircle className="h-4 w-4" />,
        duration: 3000,
      });
      e.target.value = '';
      return;
    }

    // Validate file size (5MB limit) - show error only for oversized files
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large', {
        description: 'Please select an image smaller than 5MB',
        icon: <AlertCircle className="h-4 w-4" />,
        duration: 3000,
      });
      e.target.value = '';
      return;
    }

    // Clean up previous preview URL
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    // Immediate preview - show preview first, then upload
    const previewUrl = URL.createObjectURL(file);
    previewUrlRef.current = previewUrl;
    setCurrentPhoto(previewUrl);
    setIsImageLoading(true);
    
    // Start upload after a brief delay to ensure preview is shown
    setIsUploading(true);

    try {
      await onUpdate(file);
      // Success - parent will update profilePhoto prop, which will trigger useEffect
      // No toast needed here to avoid noise
    } catch (err) {
      // Only show error toast for actual upload failures, not validation errors
      // Check if it's a network error or server error
      const isNetworkError = err instanceof TypeError || err.message?.includes('fetch');
      const errorMessage = err?.error || err?.message || 'Failed to upload profile picture';
      
      // Only show user-facing errors, suppress technical errors in console
      if (!isNetworkError || errorMessage !== 'Failed to fetch') {
        toast.error('Upload failed', {
          description: typeof errorMessage === 'string' && errorMessage.length < 100 
            ? errorMessage 
            : 'Failed to upload profile picture. Please try again.',
          icon: <AlertCircle className="h-4 w-4" />,
          duration: 4000,
        });
      }
      
      // Revert to previous photo or default
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setCurrentPhoto(profilePhoto || defaultImage());
    } finally {
      setIsUploading(false);
      setIsImageLoading(false);
      // Don't clear the input value here - let user try again if needed
      // e.target.value = '';
    }
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const handleImageError = (e) => {
    setIsImageLoading(false);
    // Silently handle image load errors - don't show toasts for image loading issues
    // Only fallback to default if it's not a blob URL (which should work)
    if (!currentPhoto || (!currentPhoto.startsWith('blob:') && currentPhoto !== defaultImage())) {
      // If it's a blob URL that failed, there might be an issue with the file
      // But don't show error to user - just fallback silently
      if (currentPhoto && currentPhoto.startsWith('blob:')) {
        // Silently handle - might be a temporary issue
        console.warn('Image preview failed to load');
      }
      setCurrentPhoto(defaultImage());
    }
  };

  const getProfilePhotoUrl = () => {
    if (!currentPhoto) return defaultImage();
    // Don't modify blob URLs (preview URLs)
    if (currentPhoto.startsWith('blob:')) {
      return currentPhoto;
    }
    // Cache-bust non-presigned URLs
    if (currentPhoto.includes('amazonaws.com')) {
      const sep = currentPhoto.includes('?') ? '&' : '?';
      return `${currentPhoto}${sep}t=${Date.now()}`;
    }
    return currentPhoto;
  };


  return (
    <div className="relative inline-block group">
      {/* File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
        disabled={isUploading}
        id="profile-picture-input"
        aria-label="Upload profile picture"
      />

      {/* Clickable Avatar */}
      <button
        type="button"
        onClick={handleButtonClick}
        disabled={isUploading}
        className={cn(
          "relative inline-block rounded-full transition-all duration-300",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background",
          "hover:scale-105 active:scale-95",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        )}
        aria-label="Upload profile picture"
      >
        <Avatar className={cn(
          "w-20 h-20 border-2 border-border shadow-lg",
          "transition-all duration-300",
          "group-hover:border-primary/70 group-hover:shadow-xl"
        )}>
          <AvatarImage
            src={getProfilePhotoUrl()}
            alt="Profile picture"
            className={cn(
              "object-cover transition-opacity duration-300",
              isImageLoading && "opacity-50"
            )}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
          <AvatarFallback className="bg-muted text-muted-foreground">
            <User className="w-8 h-8" />
          </AvatarFallback>
        </Avatar>

        {/* Upload Icon Overlay - Shows on hover */}
        {!isUploading && !isImageLoading && (
          <div className={cn(
            "absolute inset-0 rounded-full bg-primary/80 backdrop-blur-sm",
            "flex items-center justify-center",
            "opacity-0 group-hover:opacity-100 transition-opacity duration-300",
            "pointer-events-none z-10"
          )}>
            <Camera className="w-5 h-5 text-primary-foreground" />
          </div>
        )}

        {/* Uploading Overlay */}
        {isUploading && (
          <div className="absolute inset-0 rounded-full bg-background/90 backdrop-blur-sm flex flex-col items-center justify-center z-20 pointer-events-none">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        )}

        {/* Loading Overlay */}
        {isImageLoading && !isUploading && (
          <div className="absolute inset-0 rounded-full bg-background/70 backdrop-blur-sm flex items-center justify-center z-20 pointer-events-none">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </button>
    </div>
  );
}